// DatePicker — date / date-range input on the React Aria foundation (ADR-029),
// per COMPONENTS §Catalog. We own 100% of the markup + tokens; React Aria owns
// the segmented-field editing model, calendar keyboard navigation, focus,
// collision-aware popover positioning, and RTL.
//
// CALENDAR IS ALWAYS GREGORIAN (ADR-030 — no Hijri, even under `ar`). React Aria
// picks a calendar from the resolved locale's region default, and `ar`/`ar-SA`
// would default to islamic-umalqura. We never trust that default: the primitive
// self-wraps an <I18nProvider> with a locale string carrying `-u-ca-gregory`
// (plus the numerals system), built from the SAME resolveLocale() that lib/
// format.ts uses. Forcing it here — not at an app-level provider — makes the
// primitive correct by construction: no ancestor edit can render it Hijri.
//
// VALUE CONTRACT — the public value is a plain ISO `YYYY-MM-DD` string (range:
// `{ start, end }` of ISO strings). Consumers (RHF + Zod, packages/schemas)
// never touch @internationalized/date; the primitive bridges string ⇄
// CalendarDate internally. A CalendarDate is timezone-less by construction, so
// the UTC-midnight-shift class of bug is structurally impossible here.
//
// MODES (one `range` discriminant, mirroring the picker's `multiple`):
//   - range omitted/false  → single date.   `calendar` (default true) toggles
//     the calendar-popover face (DatePicker) vs the calendar-less segmented
//     field (DateField) for pure keyboard entry.
//   - range                → date range. Always carries the calendar (React
//     Aria has no two-field range field).
//
// RHF: bind via <Controller> (FRONTEND-PATTERNS §Forms). Validation is
// form-state-level (ADR-008); the `error` prop renders the visual message.
//
// THE CALENDAR'S TWO FACES (ADR-080 §12): on a desk the anchored popover; on a
// phone (the `md` tier) the family's `Sheet` from the bottom, the field's label
// as its heading — the sheet reads the picker's own open state (no `open`
// given), so the calendar button, Escape and a pick behave as on a desk. Cells
// are finger-sized under a coarse pointer.
//
// THE WEEK AND THE DAY (ADR-082 §4 · §2): the grid's first column is the
// PROJECT's week start (`weekStart` — Sunday in the Gulf, Monday in the Levant),
// never the language's; "today" is ringed in the SCREEN's zone (`zone` — the
// project's, a site's). A host that has not threaded them yet gets the locale's
// week and the browser's day — a hint on a grid, never a cut.
//
// THE INLINE FACE — `RangeCalendarInline`: the same grid without a field or a
// popover, for a host that owns its panel (the period picker's "Custom" face);
// its cells size to the host's width (a container query), finger-sized under a
// coarse pointer. A single-day grid comes with its first consumer.

import { useId, useRef, useEffect, type ReactNode } from 'react';
import {
  DatePicker as AriaDatePicker,
  DateRangePicker as AriaDateRangePicker,
  DateField as AriaDateField,
  DateInput,
  DateSegment,
  Group,
  Label as AriaLabel,
  Button as AriaButton,
  Popover as AriaPopover,
  Dialog,
  Calendar,
  RangeCalendar,
  CalendarGrid,
  CalendarCell,
  Heading,
  type DateValue,
} from 'react-aria-components';
import { I18nProvider } from 'react-aria-components';
import { icons } from './icons.js';
import { useTranslation } from 'react-i18next';
import { CalendarDate, parseDate, today, getLocalTimeZone } from '@internationalized/date';
import type { Language } from '@ems-app/schemas/common/language';
import type { Numerals } from '@ems-app/schemas/common/numerals';
import type { WeekStart } from '@ems-app/schemas/common/period';
import { resolveLocale, getFormatLocale } from '../../lib/format.js';
import { useMediaQuery } from '../../hooks/useMediaQuery.js';
import { PHONE_QUERY, Sheet } from './Sheet.js';

// ── locale ──────────────────────────────────────────────────────────────────

// Build the React Aria locale: base locale + Gregorian + numerals. `-u-ca-gregory`
// is the ADR-030 guard — it forces the calendar Gregorian regardless of the
// locale's region default (ar-SA → islamic-umalqura otherwise). Reuses the same
// resolveLocale() the rest of the app formats through. Pure → unit-tested.
export function buildAriaDateLocale(language: Language, numerals: Numerals): string {
  const { locale, numberingSystem } = resolveLocale(language, numerals);
  return `${locale}-u-ca-gregory-nu-${numberingSystem}`;
}

// Language is reactive (useTranslation re-renders on language change); numerals
// comes from the format module's ambient (server-set via /me) — it changes
// rarely and is re-read on the next language-driven render.
function useAriaDateLocale(): string {
  const { i18n } = useTranslation();
  const language = (['en', 'fr', 'ar'].includes(i18n.language) ? i18n.language : 'en') as Language;
  const { numerals } = getFormatLocale();
  return buildAriaDateLocale(language, numerals);
}

// ── the week start, the day ───────────────────────────────────────────────────

const DAY_OF_WEEK: Record<WeekStart, 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'> = { 1: 'mon', 2: 'tue', 3: 'wed', 4: 'thu', 5: 'fri', 6: 'sat', 7: 'sun' };

/** The ISO week start (1 = Monday … 7 = Sunday — the project's setting) as the calendar takes it. */
function firstDayOf(weekStart: WeekStart | undefined) {
  return weekStart === undefined ? undefined : DAY_OF_WEEK[weekStart];
}

/** Today in the screen's zone — the ring on the grid (the browser's day when no zone is given: a hint, never a cut). */
function todayIn(zone: string | undefined): CalendarDate {
  return today(zone ?? getLocalTimeZone());
}

// ── string ⇄ CalendarDate bridge ──────────────────────────────────────────────

function toCalendarDate(iso: string | null | undefined): CalendarDate | null {
  if (!iso) return null;
  try {
    return parseDate(iso); // ISO YYYY-MM-DD → CalendarDate (gregorian)
  } catch {
    return null;
  }
}

// ── shared props ──────────────────────────────────────────────────────────────

interface DatePickerBaseProps {
  /** Visible label above the field (rendered internally, like PickerField). */
  label?: string;
  /** Accessible name when there is no visible label. */
  ariaLabel?: string;
  required?: boolean;
  /** Error message below the field. Linked via aria-describedby. */
  error?: string;
  disabled?: boolean;
  /** Earliest selectable date, ISO `YYYY-MM-DD`. Constrains segments + calendar. */
  minValue?: string;
  /** Latest selectable date, ISO `YYYY-MM-DD`. */
  maxValue?: string;
  /** Mark specific in-range dates unselectable (greyed in the calendar, rejected
   *  in the segmented field). ISO `YYYY-MM-DD` in; e.g. the change surface greys
   *  existing period boundaries (a change ON a boundary is a correction). */
  isDateUnavailable?: (iso: string) => boolean;
  /** Focus the segmented field on mount — for a drawer/dialog whose date IS the
   *  primary field (e.g. Update-config / Correct-start-date). Imperative (a ref
   *  .focus() in an effect), not the HTML autofocus attribute — jsx-a11y-clean. */
  focusOnMount?: boolean;
  /** Forces the calendar's face (stories; the gate runs at one viewport). Default: the `md` tier. */
  face?: 'desk' | 'phone';
  /** The grid's first day — the PROJECT's week start (ADR-082 §4). Absent: the locale's. */
  weekStart?: WeekStart;
  /** The zone "today" is ringed in — the SCREEN's (ADR-082 §2). Absent: the browser's day. */
  zone?: string;
  className?: string;
}

export interface SingleDatePickerProps extends DatePickerBaseProps {
  range?: false;
  /** false → calendar-less DateField (pure keyboard entry). Default true. */
  calendar?: boolean;
  value: string | null;
  onChange: (value: string | null) => void;
}

export interface RangeDatePickerProps extends DatePickerBaseProps {
  range: true;
  value: { start: string; end: string } | null;
  onChange: (value: { start: string; end: string } | null) => void;
}

export type DatePickerProps = SingleDatePickerProps | RangeDatePickerProps;

// ── styling (mirrors Input / PickerField) ────────────────────────────────────────

// Border + hover + focus light-up + invalid: the shared `field-frame` utility
// (DESIGN-TOKENS §Interaction states); the segments' aria-invalid drives the
// error border via its :has().
const frameBase =
  'flex items-center gap-1 px-3 rounded-md field-frame transition-colors bg-[var(--color-bg-page)] ' +
  // RAC sets data-disabled on the root (the `group`), not this Group element.
  'group-data-[disabled]:bg-[var(--color-bg-surface-alt)] group-data-[disabled]:cursor-not-allowed group-data-[disabled]:hover:border-[var(--color-border)]';

// The focused segment is the active EDIT TARGET, not a committed selection, so
// it takes the neutral interaction state-layer (--color-bg-hover) — the same
// highlight every option / menu item / row uses; brand stays reserved for
// selection (the chosen calendar cell). DESIGN-TOKENS §Interaction states: only
// nav highlights brand. The state-layer is translucent in both modes, so it
// reads on the field surface AND keeps the digits legible (no special token).
// The frame's focus-within ring is the focus signal; this marks which segment
// is live (the SOTA filled-segment pattern).
const segmentBase =
  // pointer-coarse:text-base — one field size per form on touch (canonical
  // note: Input.tsx; segments never zoom, this is the uniformity half).
  'rounded-sm px-0.5 text-sm pointer-coarse:text-base tabular-nums outline-none ' +
  'text-[var(--color-text-primary)] ' +
  'data-[placeholder]:text-[var(--color-text-disabled)] ' +
  'data-[disabled]:text-[var(--color-text-disabled)] ' +
  'data-[focused]:bg-[var(--color-bg-hover)]';

const triggerBtn =
  'shrink-0 p-1.5 min-w-[2rem] min-h-[2rem] inline-flex items-center justify-center rounded-md cursor-pointer ' +
  'text-[var(--color-text-secondary)] data-[hovered]:text-[var(--color-text-primary)] data-[hovered]:bg-[var(--color-bg-hover)] data-[pressed]:bg-[var(--color-border)] ' +
  'outline-none focus-ring-inset data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed';

const navBtn =
  'p-1.5 min-w-[2rem] min-h-[2rem] inline-flex items-center justify-center rounded-md cursor-pointer ' +
  'text-[var(--color-text-secondary)] data-[hovered]:text-[var(--color-text-primary)] data-[hovered]:bg-[var(--color-bg-hover)] data-[pressed]:bg-[var(--color-border)] ' +
  'outline-none focus-ring-inset data-[disabled]:opacity-40 data-[disabled]:cursor-not-allowed';

const popover =
  'z-dropdown rounded-md border border-[var(--color-border)] bg-[var(--color-bg-overlay)] shadow-overlay ' +
  'data-[entering]:o-pop-in data-[exiting]:o-pop-out';

// ── building blocks ────────────────────────────────────────────────────────────

function Lbl({ label, required }: { label?: string; required?: boolean }) {
  if (!label) return null;
  return (
    <AriaLabel className="block text-sm font-medium text-[var(--color-text-primary)] mb-1">
      {label}
      {required && (
        <span aria-hidden="true" className="text-[var(--color-danger)] ms-0.5">
          *
        </span>
      )}
    </AriaLabel>
  );
}

function Err({ id, error }: { id: string; error?: string }) {
  if (!error) return null;
  return (
    <p id={id} className="mt-1 text-xs text-[var(--color-danger)]">
      {error}
    </p>
  );
}

function segments() {
  return (
    <DateInput className="flex flex-1 min-w-0">
      {(segment) => <DateSegment segment={segment} className={segmentBase} />}
    </DateInput>
  );
}

/** The cell size an INLINE grid takes from its host: at most the pointer's size, never wider than a seventh of the host (a
 *  `@container` ancestor) less the grid's spacing — so a coarse pointer's 44px cells never spill past a narrow panel. */
const INLINE_CELL = '[--cell:min(2.25rem,calc((100cqw-1rem)/7))] pointer-coarse:[--cell:min(2.75rem,calc((100cqw-1rem)/7))]';
const INLINE_CELL_TOUCH = '[--cell:min(2.75rem,calc((100cqw-1rem)/7))]';

function calendarGrid(todayDate: CalendarDate, touch = false, inline = false) {
  return (
    <CalendarGrid className="mt-2 border-separate border-spacing-0.5">
      {(date) => (
        <CalendarCell
          date={date}
          className={({ isSelected, isDisabled, isUnavailable, isOutsideMonth, isHovered }) => {
            // A blocked cell must READ as non-clickable — muted, no hover, no pointer.
            // isDisabled = out of the min/max range; isUnavailable = a blocked in-range
            // date (e.g. an existing period boundary), given a strike so it's obviously
            // "this specific date is taken" rather than merely out of range. The colour
            // arms are mutually exclusive: the old code emitted BOTH the disabled colour
            // and the trailing primary colour, so disabled cells rendered un-greyed.
            const blocked = isDisabled || isUnavailable;
            return [
              'flex items-center justify-center rounded-md outline-none tabular-nums',
              inline
                ? `size-[var(--cell)] ${touch ? 'text-base' : 'text-sm pointer-coarse:text-base'}`
                : touch
                  ? 'w-11 h-11 text-base'
                  : 'w-9 h-9 pointer-coarse:w-11 pointer-coarse:h-11 text-sm pointer-coarse:text-base',
              'focus-ring',
              blocked ? 'cursor-not-allowed' : 'cursor-pointer',
              isSelected
                ? 'bg-[var(--color-primary-display)] text-[var(--color-on-primary-display)]'
                : isUnavailable
                  ? 'text-[var(--color-text-disabled)] line-through'
                  : isDisabled
                    ? 'text-[var(--color-text-disabled)] opacity-60'
                    : isOutsideMonth
                      ? 'text-[var(--color-text-disabled)] opacity-50'
                      : isHovered
                        ? 'bg-[var(--color-bg-hover)] text-[var(--color-text-primary)]'
                        : 'text-[var(--color-text-primary)]',
              // today: a ring when not the selected cell (never on a blocked cell).
              !isSelected && !blocked && date.compare(todayDate) === 0
                ? 'ring-1 ring-inset ring-[var(--color-primary-display)]'
                : '',
            ]
              .filter(Boolean)
              .join(' ');
          }}
        />
      )}
    </CalendarGrid>
  );
}

// A plain container, not a <header>: inline on a page the calendar's own header
// would be a BANNER landmark inside another landmark (an axe fault); the month
// heading and the two buttons carry their own names.
function calendarHeader(t: (k: string) => string) {
  return (
    <div className="flex items-center justify-between gap-2 px-1">
      <AriaButton slot="previous" aria-label={t('datepicker.previous')} className={navBtn}>
        <icons.caretLeft aria-hidden size={16} className="rtl:rotate-180" />
      </AriaButton>
      <Heading className="text-sm font-medium text-[var(--color-text-primary)]" />
      <AriaButton slot="next" aria-label={t('datepicker.next')} className={navBtn}>
        <icons.caretRight aria-hidden size={16} className="rtl:rotate-180" />
      </AriaButton>
    </div>
  );
}

// ── component ────────────────────────────────────────────────────────────────

export function DatePicker(props: DatePickerProps) {
  const { label, ariaLabel, required = false, error, disabled = false, minValue, maxValue, isDateUnavailable, focusOnMount, face, weekStart, zone, className = '' } = props;
  const { t } = useTranslation('common');
  const locale = useAriaDateLocale();
  const narrow = useMediaQuery(PHONE_QUERY);
  const phone = face ? face === 'phone' : narrow;
  // The calendar's host — the anchored popover, or the sheet from the bottom (the field's words as its heading).
  const host = (calendar: ReactNode) =>
    phone ? (
      <Sheet title={label ?? ariaLabel ?? t('datepicker.calendar')} face="bottom" className="items-center">
        {calendar}
      </Sheet>
    ) : (
      <AriaPopover offset={4} className={popover}>
        <Dialog className="p-3 outline-none">{calendar}</Dialog>
      </AriaPopover>
    );
  const reactId = useId();
  const errorId = error ? `${reactId}-error` : undefined;
  const todayDate = todayIn(zone);
  const firstDayOfWeek = firstDayOf(weekStart);
  // Imperative focus-on-mount (jsx-a11y-clean vs the autoFocus attribute): land on
  // the first editable segment when the date is a dialog's primary input.
  const groupRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focusOnMount) groupRef.current?.querySelector<HTMLElement>('[role="spinbutton"], input')?.focus();
  }, [focusOnMount]);

  const shared = {
    isDisabled: disabled,
    isInvalid: !!error,
    isRequired: required,
    minValue: toCalendarDate(minValue) ?? undefined,
    maxValue: toCalendarDate(maxValue) ?? undefined,
    // Bridge ISO ⇄ CalendarDate at the boundary so consumers stay string-only
    // (VALUE CONTRACT). React Aria forwards this to the inner Calendar/RangeCalendar
    // and also uses it to invalidate typed segment entry.
    isDateUnavailable: isDateUnavailable ? (d: DateValue) => isDateUnavailable(d.toString()) : undefined,
    'aria-label': label ? undefined : ariaLabel,
    'aria-describedby': errorId,
    className: `group w-full ${className}`,
  };


  // ── range ──
  if (props.range) {
    return (
      <I18nProvider locale={locale}>
        <AriaDateRangePicker
          {...shared}
          value={
            props.value
              ? { start: toCalendarDate(props.value.start)!, end: toCalendarDate(props.value.end)! }
              : null
          }
          onChange={(v) =>
            props.onChange(v ? { start: v.start.toString(), end: v.end.toString() } : null)
          }
        >
          <Lbl label={label} required={required} />
          <Group ref={groupRef} className={`${frameBase}`}>
            <DateInput slot="start" className="flex min-w-0">
              {(segment) => <DateSegment segment={segment} className={segmentBase} />}
            </DateInput>
            <span aria-hidden className="px-1 text-[var(--color-text-secondary)]">
              –
            </span>
            <DateInput slot="end" className="flex flex-1 min-w-0">
              {(segment) => <DateSegment segment={segment} className={segmentBase} />}
            </DateInput>
            <AriaButton aria-label={t('datepicker.calendar')} className={triggerBtn}>
              <icons.calendar aria-hidden size={16} />
            </AriaButton>
          </Group>
          <Err id={errorId!} error={error} />
          {host(
            <RangeCalendar firstDayOfWeek={firstDayOfWeek}>
              {calendarHeader(t)}
              {calendarGrid(todayDate)}
            </RangeCalendar>,
          )}
        </AriaDateRangePicker>
      </I18nProvider>
    );
  }

  // ── single, calendar-less (DateField) ──
  if (props.calendar === false) {
    return (
      <I18nProvider locale={locale}>
        <AriaDateField
          {...shared}
          value={toCalendarDate(props.value)}
          onChange={(v: DateValue | null) => props.onChange(v ? v.toString() : null)}
        >
          <Lbl label={label} required={required} />
          <Group ref={groupRef} className={`${frameBase}`}>{segments()}</Group>
          <Err id={errorId!} error={error} />
        </AriaDateField>
      </I18nProvider>
    );
  }

  // ── single, with calendar (default) ──
  return (
    <I18nProvider locale={locale}>
      <AriaDatePicker
        {...shared}
        value={toCalendarDate(props.value)}
        onChange={(v: DateValue | null) => props.onChange(v ? v.toString() : null)}
      >
        <Lbl label={label} required={required} />
        <Group ref={groupRef} className={`${frameBase}`}>
          {segments()}
          <AriaButton aria-label={t('datepicker.calendar')} className={triggerBtn}>
            <icons.calendar aria-hidden size={16} />
          </AriaButton>
        </Group>
        <Err id={errorId!} error={error} />
        {host(
          <Calendar firstDayOfWeek={firstDayOfWeek}>
            {calendarHeader(t)}
            {calendarGrid(todayDate)}
          </Calendar>,
        )}
      </AriaDatePicker>
    </I18nProvider>
  );
}

// ── the inline faces — the grid alone, for a host that owns its panel ─────────

interface InlineCalendarBaseProps {
  /** Accessible name of the grid (no visible label — the host's panel names it). */
  ariaLabel: string;
  minValue?: string;
  maxValue?: string;
  isDateUnavailable?: (iso: string) => boolean;
  weekStart?: WeekStart;
  zone?: string;
  /** Finger-sized cells (a phone host). Default: by the pointer. */
  touch?: boolean;
  className?: string;
}

export interface RangeCalendarInlineProps extends InlineCalendarBaseProps {
  value: { start: string; end: string } | null;
  onChange: (value: { start: string; end: string } | null) => void;
  /** The day whose month the grid shows when no value is set (a typed start) — read at mount; key the grid to move it. */
  focusedDay?: string;
}

/** Two days picked on one grid, in place — the period picker's "Custom" face. The host is a `@container`: the cells
 *  size to its width. */
export function RangeCalendarInline({ ariaLabel, value, onChange, focusedDay, minValue, maxValue, isDateUnavailable, weekStart, zone, touch = false, className = '' }: RangeCalendarInlineProps) {
  const { t } = useTranslation('common');
  const locale = useAriaDateLocale();
  return (
    <I18nProvider locale={locale}>
      <RangeCalendar
        aria-label={ariaLabel}
        value={value ? { start: toCalendarDate(value.start)!, end: toCalendarDate(value.end)! } : null}
        defaultFocusedValue={toCalendarDate(focusedDay) ?? undefined}
        onChange={(v) => onChange(v ? { start: v.start.toString(), end: v.end.toString() } : null)}
        minValue={toCalendarDate(minValue) ?? undefined}
        maxValue={toCalendarDate(maxValue) ?? undefined}
        isDateUnavailable={isDateUnavailable ? (d: DateValue) => isDateUnavailable(d.toString()) : undefined}
        firstDayOfWeek={firstDayOf(weekStart)}
        className={`outline-none ${touch ? INLINE_CELL_TOUCH : INLINE_CELL} ${className}`}
      >
        {calendarHeader(t)}
        {calendarGrid(todayIn(zone), touch, true)}
      </RangeCalendar>
    </I18nProvider>
  );
}

